import { z } from 'zod';

export const connectionSyncToTimelyEnums = ['on_demand', 'continuous'] as const;
export const ConnectionSyncTimelyEnumsSchema = z.enum(connectionSyncToTimelyEnums);

/**
 * Single source of truth for sync setting keys
 * All other sync-related objects derive from this.
 */
const supportedSyncsToTimelyTypes = [
  'sync_projects',
  'sync_tasks',
  'reference_tasks',
  'sync_tags',
  'sync_clients',
  'sync_teams',
  'sync_users',
  'sync_memories',
  'sync_time_entries',
] as const;

export const SupportedSyncToTimelyEnumsSchema = z.enum(supportedSyncsToTimelyTypes);
export type SupportedSyncToTimelyEnums = z.infer<typeof SupportedSyncToTimelyEnumsSchema>;

export const CustomFieldMappingTargetResourceTypeSchema = z.enum(['client']);
export type CustomFieldMappingTargetResourceType = z.infer<
  typeof CustomFieldMappingTargetResourceTypeSchema
>;

export const CustomFieldMappingContextValueSchema = z.union([z.string(), z.number(), z.boolean()]);

export const CustomFieldMappingContextSchema = z
  .record(z.string(), CustomFieldMappingContextValueSchema)
  .default({})
  .describe(
    'Provider-specific context required to read this field again during import; providers validate their executable context shape before using a mapping'
  );

export const CustomFieldMappingSchema = z.object({
  id: z.string().min(1).describe('Provider-scoped mapping identifier'),
  field_id: z.string().min(1).describe('Provider custom field identifier'),
  target_resource_type: CustomFieldMappingTargetResourceTypeSchema.describe(
    'The TIC resource type produced from this provider custom field'
  ),
  context: CustomFieldMappingContextSchema,
});
export type CustomFieldMapping = z.infer<typeof CustomFieldMappingSchema>;

export const CustomFieldMappingArraySchema = z.preprocess(
  value => (value == null ? [] : value),
  z.array(CustomFieldMappingSchema)
);

/**
 * Connection Settings are used to store end-user preferences and settings.
 * such as controlling access to data, what/how to sync with Timely, etc.
 *
 * @comment Please bump `version` by +1 when adding new properties to this schema.
 */
export type ConnectionSettings = z.infer<typeof ConnectionSettingsSchema>;
export const ConnectionSettingsSchema = z.object({
  version: z.coerce
    .number()
    .default(8)
    .describe(
      'Version of the connection settings schema; Increment this when making changes to the schema to simplify SQL migrations that backfill existing connections with new settings.'
    ),
  auto_subscribe: z.coerce.boolean().default(true).describe('Should new data be auto-subscribed?'),

  import_data_detail_level: z
    .enum(['default', 'minimum'])
    .default('default')
    .describe(
      'How much of the imported data should be stored? Set to minimum for only the essentials.'
    ),
  import_profile_key: z
    .string()
    .min(1)
    .default('default')
    .describe(
      'Profile key used to scope imported resources (e.g. Trello board vs. client hierarchy).'
    ),
  setup_completed: z.coerce
    .boolean()
    .default(false)
    .describe('Whether setup wizard is complete and data diff dumping to Timely should be active'),
  setup_settings_configured: z
    .boolean()
    .optional()
    .describe(
      'Internal wizard marker: whether initial sync settings defaults have been written to this connection. Used to avoid re-applying operation defaults when resuming setup.'
    ),
  sync_to_timely: ConnectionSyncTimelyEnumsSchema.nullable()
    .default('continuous')
    .describe(
      'How should the connection sync with Timely? `null` disables syncing (uninstalling any webhooks as well), `on_demand` has no webhooks and dumps once to Timely, `continuous` installs webhooks and creates content in Timely automatically'
    ),
  // Sync properties - ensures all SyncToTimelySettingKeys are included
  sync_projects: z.coerce.boolean().default(false).describe('Should projects sync to Timely?'),
  sync_tasks: z.coerce.boolean().default(false).describe('Should tasks be synced to Timely?'),
  reference_tasks: z.coerce
    .boolean()
    .default(false)
    .describe(
      'Should tasks be available as **references** when logging time? Does NOT require `sync_tasks` to be enabled. Timely tasks are not created.'
    ),
  sync_tags: z.coerce.boolean().default(false).describe('Should labels be synced to Timely?'),
  sync_clients: z.coerce.boolean().default(true).describe('Should clients sync to Timely?'),
  sync_teams: z.coerce.boolean().default(false).describe('Should teams sync to Timely?'),
  sync_users: z.coerce.boolean().default(false).describe('Should users sync to Timely?'),
  sync_memories: z.coerce.boolean().default(false).describe('Should memories be created in TIC?'),
  fallback_timely_client_id: z
    .number()
    .nullable()
    .default(null)
    .describe(
      'Default client ID to assign to imported projects when no client mapping is available'
    ),
  custom_field_mappings: CustomFieldMappingArraySchema.describe(
    'Provider custom fields that should produce or resolve TIC resource relationships'
  ),
  sync_time_entries: z.coerce
    .boolean()
    .default(false)
    .describe('Should time entries be synced back to the external system (e.g., Jira worklogs)?'),

  /**
   * Project sync options (flat settings schema).
   *
   * @remarks
   * These used to live under `sync_project_options.*`, but were never launched.
   * We now keep them flat to avoid nested settings handling across TIC + frontend.
   */
  project_add_all_workspace_users: z
    .boolean()
    .optional()
    .describe('Add all workspace users to imported projects'),
  project_replace_users_on_update: z
    .boolean()
    .optional()
    .describe('Replace vs merge project users on update'),
  project_send_invites: z
    .boolean()
    .optional()
    .describe('Send invites when adding users to projects'),
  project_require_labels: z
    .boolean()
    .optional()
    .describe('Set required_labels flag on imported projects'),
  project_auto_add_assignees_to_projects: z
    .boolean()
    .optional()
    .describe(
      'When tasks include assignees that are not members of the project in Timely, automatically add them to the project before importing the task.'
    ),
  project_default_rate_type: z
    .enum(['project', 'user', 'non-billable'])
    .optional()
    .describe('Default billing rate type for new projects'),
  restrict_tasks_to_assignees: z.coerce
    .boolean()
    .default(false)
    .describe(
      'When enabled, users only see tasks they have access to via their personal ClickUp connection (direct assignee, ancestor task assignee, or project member). Fail-closed: users without a personal connection see no tasks.'
    ),
});
